import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Flex, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { isApeCode } from '@shinetools/ape-code-library';
import { isSIRET } from '@shinetools/vat-siren-siret';
import { isPast, isValid as isValidDate, parseISO } from 'date-fns';
import { z } from 'zod';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import zodLocales from 'common/zod/locales';
import * as Form from 'components/_core/form';
import OBBackButton from 'features/Onboarding/components/OBBackButton';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';
import { formatDate, YYYY_MM_DD } from 'helpers/date';

import Header from '../../../../components/Header';
import { Context } from '../../machine/model';
import { hybridSiretInput } from '../../utils/hybridSiretUtils';
import locales from './locales';

const today = new Date();
const formattedToday = formatDate(today, YYYY_MM_DD);

const ZODlegalInfoSchema = z.object({
  activityType: z
    .string()
    .min(1, { message: zodLocales.errors.required })
    .regex(/^\d{4}[A-Za-z]$/, {
      message: locales.inputs.nafCode.errors.invalidFormat,
    })
    .refine((value) => isApeCode(value), {
      message: locales.inputs.nafCode.errors.invalidCode,
    }),
  legalRegistrationNumber: z
    .string()
    .min(1, { message: zodLocales.errors.required })
    .refine((value) => isSIRET(value), {
      message: locales.inputs.siret.errors.invalid,
    }),
  registrationDate: z
    .string()
    .refine((value) => isValidDate(parseISO(value ?? '')), {
      message: locales.inputs.registrationDate.errors.invalid,
    })
    .refine((value) => isPast(parseISO(value ?? '')), {
      message: locales.inputs.registrationDate.errors.maxValue,
    }),
});

export type LegalInfoPayload = z.infer<typeof ZODlegalInfoSchema>;

interface LegalInfoEditProps {
  company: Context['companyProfile'];
  isEditing: boolean;
  onContinue: (payload: LegalInfoPayload) => void;
}

const LegalInfoEdit = asBentoPage<LegalInfoEditProps>(
  ({ company, isEditing, Layout, onContinue, onPrev }) => {
    const {
      control,
      formState: { errors, isValid },
      handleSubmit,
      register,
      trigger,
    } = useForm<LegalInfoPayload>({
      defaultValues: {
        activityType: company.activityType,
        legalRegistrationNumber: company.legalRegistrationNumber,
        registrationDate: company.registrationDate ?? '',
      },
      mode: 'onChange',
      resolver: zodResolver(ZODlegalInfoSchema),
    });

    useEffect(() => {
      if (
        company.activityType ||
        company.legalRegistrationNumber ||
        company.registrationDate
      ) {
        trigger();
      }
    }, [company, trigger]);

    const onSubmit = handleSubmit((values) => {
      onContinue(values);
    });

    return (
      <Layout
        data-testid="legal-info-edit"
        header={<OBBackButton onClick={onPrev} />}
        nav={<OBNavigation onPrev={onPrev} />}
      >
        <Flex direction="column" height={{ base: 'full', md: 'auto' }}>
          <Header title={locales.title[isEditing ? 'edit' : 'add']} />

          <Flex
            as="form"
            data-testid="business-address-edit-form"
            direction="column"
            height="full"
            justify="space-between"
            onSubmit={onSubmit}
          >
            <VStack alignItems="stretch" spacing="space-24">
              <Form.Field
                error={errors.registrationDate?.message}
                label={locales.inputs.registrationDate.label}
              >
                <Controller
                  control={control}
                  name="registrationDate"
                  render={({ field: { onChange, value, ...fieldRest } }) => (
                    <Form.Input
                      {...fieldRest}
                      data-testid="registration-date-input"
                      max={formattedToday}
                      onChange={(e) => onChange(e.target.value)}
                      required
                      type="date"
                      value={value}
                    />
                  )}
                />
              </Form.Field>

              <Form.Field
                error={errors.legalRegistrationNumber?.message}
                hint={locales.inputs.siret.help}
                label={locales.inputs.siret.label}
              >
                <Controller
                  control={control}
                  name="legalRegistrationNumber"
                  render={({ field: { onChange, value, ...fieldRest } }) => (
                    <Form.Input
                      {...fieldRest}
                      data-testid="siret-input"
                      inputMode="numeric"
                      onChange={(e) =>
                        onChange(hybridSiretInput.unmask(e.target.value))
                      }
                      required
                      value={hybridSiretInput.mask(value)}
                    />
                  )}
                />
              </Form.Field>

              <Form.Field
                error={errors.activityType?.message}
                hint={locales.inputs.nafCode.help}
                label={locales.inputs.nafCode.label}
              >
                <Form.Input
                  {...register('activityType')}
                  data-testid="naf-code-input"
                />
              </Form.Field>
            </VStack>

            <OBStickyButton
              isDisabled={!isValid}
              justifyContent="left"
              type="submit"
            >
              {isEditing ? locales.save : locales.continue}
            </OBStickyButton>
          </Flex>
        </Flex>
      </Layout>
    );
  },
);

export default LegalInfoEdit;
